import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, X } from 'lucide-react';
import { getRecentlyViewed, clearRecentlyViewed } from '../utils/recentlyViewed';
import { getProductUrl } from '../utils/url-helpers';
import { formatPrice } from '../utils/formatters';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../config/translations';

function RecentlyViewed() {
  const { language, languageConfig } = useLanguage();
  const t = (key) => getTranslation(language, key);
  const [recentProducts, setRecentProducts] = useState([]);

  useEffect(() => {
    setRecentProducts(getRecentlyViewed());
  }, []);

  const handleClear = () => {
    clearRecentlyViewed();
    setRecentProducts([]);
  };

  if (recentProducts.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Clock className="w-5 h-5 text-primary-600" />
          <h3 className="text-lg font-bold text-gray-900">{t('recentlyViewed')}</h3>
        </div>
        <button
          onClick={handleClear}
          className="text-sm text-gray-600 hover:text-gray-900 flex items-center space-x-1"
          aria-label={t('clearHistory')}
        >
          <X className="w-4 h-4" aria-hidden="true" />
          <span>{t('clear')}</span>
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {recentProducts.map((product) => {
          const priceRange = product.minPrice === product.maxPrice
            ? formatPrice(product.minPrice, product.currency, languageConfig.locale)
            : `${formatPrice(product.minPrice, product.currency, languageConfig.locale)} - ${formatPrice(product.maxPrice, product.currency, languageConfig.locale)}`;

          return (
            <Link
              key={product.id}
              to={getProductUrl(product)}
              className="card p-3 hover:shadow-md transition-shadow group"
            >
              {/* Product Image */}
              <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-200 rounded mb-2 overflow-hidden">
                {product.image ? (
                  <img
                    src={product.image}
                    alt={product.name}
                    width="200"
                    height="200"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Clock className="w-8 h-8 text-gray-500" aria-hidden="true" />
                  </div>
                )}
              </div>

              {/* Brand */}
              {product.brand && (
                <p className="text-xs font-medium text-primary-600 uppercase tracking-wide mb-1 truncate">
                  {product.brand}
                </p>
              )}

              {/* Product Name */}
              <p className="text-sm font-medium text-gray-900 line-clamp-2 group-hover:text-primary-600 transition-colors mb-1">
                {product.name}
              </p>

              {/* Price */}
              <p className="text-sm font-bold text-gray-900">
                {priceRange}
              </p>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export default RecentlyViewed;
